import React, { useEffect, useState } from "react";
import { Building2, Users, DollarSign, MessageSquare, Package, Loader2, TrendingUp } from "lucide-react";
import { owner } from "@/lib/owner-api";
import { formatIDR, formatNumber } from "@/lib/format";

const StatCard = ({ icon: Icon, label, value, sub, color = "emerald", testid }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid={testid}>
    <div className={`w-10 h-10 rounded-lg bg-${color}-900/30 border border-${color}-800 flex items-center justify-center text-${color}-400`}>
      <Icon size={18} />
    </div>
    <div className="mt-4 text-xs font-bold uppercase tracking-widest text-gray-500">{label}</div>
    <div className="mt-1 font-heading text-3xl font-bold text-white">{value}</div>
    {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
  </div>
);

export default function OwnerOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    owner.overview().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-emerald-400" size={28} /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Platform Overview</h1>
        <p className="text-sm text-gray-500 mt-1">Ringkasan performa seluruh Custora platform.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-xl p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div className="relative">
            <div className="text-xs font-bold tracking-widest uppercase text-emerald-200">Monthly Recurring Revenue</div>
            <div className="mt-2 font-heading text-5xl sm:text-6xl font-bold">{formatIDR(data?.mrr || 0)}</div>
            <div className="mt-4 flex items-center gap-6 text-sm">
              <div><div className="text-emerald-200 text-xs">ARR</div><div className="font-semibold text-lg">{formatIDR(data?.arr || 0)}</div></div>
              <div><div className="text-emerald-200 text-xs">Total Merchants</div><div className="font-semibold text-lg">{formatNumber(data?.total_merchants || 0)}</div></div>
              <div><div className="text-emerald-200 text-xs">Active</div><div className="font-semibold text-lg">{formatNumber(data?.active_merchants || 0)}</div></div>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Plan Distribution</div>
          <div className="mt-3 space-y-2">
            {Object.entries(data?.plan_distribution || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between text-sm">
                <span className="capitalize text-gray-300">{plan}</span>
                <span className="font-bold text-white">{count}</span>
              </div>
            ))}
            {Object.keys(data?.plan_distribution || {}).length === 0 && <div className="text-xs text-gray-500">Belum ada data</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Total Merchant" value={formatNumber(data?.total_merchants || 0)} sub={`${data?.suspended_merchants || 0} disuspend`} color="emerald" testid="stat-merchants" />
        <StatCard icon={Users} label="Total Customer" value={formatNumber(data?.total_customers || 0)} color="blue" testid="stat-customers" />
        <StatCard icon={Package} label="QR Campaign" value={formatNumber(data?.total_qr_campaigns || 0)} color="amber" testid="stat-qr" />
        <StatCard icon={MessageSquare} label="WA Campaign" value={formatNumber(data?.total_wa_campaigns || 0)} color="purple" testid="stat-wa" />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="text-emerald-400" size={18} />
          <h3 className="font-heading font-semibold text-white">Top Merchants by Customer Count</h3>
        </div>
        <div className="mt-4 space-y-2">
          {(data?.top_merchants || []).length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">Belum ada merchant</div>
          ) : data.top_merchants.map((m, i) => (
            <div key={m.merchant.id} className="flex items-center gap-3 py-2 border-b border-gray-800 last:border-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${i === 0 ? "bg-amber-500/20 text-amber-300" : "bg-gray-800 text-gray-400"}`}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{m.merchant.business_name}</div>
                <div className="text-xs text-gray-500 truncate">{m.merchant.email} · {m.merchant.subscription_plan}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-emerald-400">{formatNumber(m.customer_count)}</div>
                <div className="text-[10px] text-gray-500">customers</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
